import FieldProto from "../../fields3/proto/field-proto"
import {ConditionType, ConditionValueType, IConditionType, IConditionTypeFactory} from "./condition-types"
import {convertToMomentDateRangeFromValue} from "../../gd/convert/moment-range"
import {fillStatusChangedConditionValue} from "../../fields3/system/task/status-changed/condition-value"
import ModelEntity from "../model-entity"
import {
    DateRangeRelativity, RELATIVE_PERIOD_DEFAULT_VALUE
} from "../../fields3/generic/datetime/date-range-relative-period/date-range-relative-period"
import {ConditionsToJsonParams} from "./@types"

export type ConditionConfig = {
    id?             : string,
    type?           : ConditionType,
    systemFieldId?  : string,
    value?          : any
}

export default class Condition {

    protected _field?   : FieldProto

    public id           : ID                // generated
    private _type       : ConditionType
    private _value      : any

    constructor(data:any) {
        this.id         = data.id
        this._field     = this.getSystemField(data)
        this._type      = data.type

        // this._value      = data.value
        this.value      = data.value    // we want to normalize it
    }

    // Prishlos proverku postavit chto bi filters reset ne vizvalo error
    getSystemField(data: any): FieldProto | undefined {
        if (!!data.systemFieldId) {
            return gd.fields.getBySystemFieldId(data.systemFieldId)
        } else {
            return undefined
        }
    }

    get value():any {
        return this._value
    }

    set value(value:any) {

        // Normalize value patch - mne stremno calendar FieldDate pravit' shodu
        const IConditionType = this._type ? IConditionTypeFactory(this._type) : undefined

        // Composite "status changed" value: fill every missing part with its default here, at model
        // level, so an untouched or partially-loaded condition is already complete (isEmpty is false,
        // the payload matches what the form shows). The form only reads it, never materializes it.
        if (this._type === ConditionType.TASK_STATUS_CHANGED) {
            this._value = fillStatusChangedConditionValue(value)
            return
        }

        if (value) {
            switch (IConditionType?.valueType) {
                case ConditionValueType.MOMENT_DATE:
                    value = momentDate(value)
                    break

                case ConditionValueType.DATE_RANGE:
                    value = convertToMomentDateRangeFromValue(value)
                    break
            }
        } else {
            switch (IConditionType?.valueType) {
                case ConditionValueType.DATE_RELATIVE_PERIOD:
                    value = RELATIVE_PERIOD_DEFAULT_VALUE
                    break
            }
        }

        this._value = value
    }

    get type() {
        return this._type
    }

    set type(newType: ConditionType) {

        if (this._type) {
            // if value type changes we need reset value
            const oldIConditionType = this._type ? IConditionTypeFactory(this._type) : undefined
            const newIConditionType = newType ? IConditionTypeFactory(newType) : undefined

            if (oldIConditionType?.valueType!=newIConditionType?.valueType) {
                this._type = newType // need to be set before updating value
                // console.log("condition value type is changed -> need reset value")
                this.value = null
            }
        }

        this._type = newType
    }

    set field(value: FieldProto | undefined) {
        this._field = value // Field object

        this.type   = (this._field?.defaultConditionType as typeof IConditionType)?.type    // take default condition type from field or first from field conditionTypes
        this.value  = null                                                                  // reset value selected previously (if any)
    }

    get field(): FieldProto | undefined {
        return this._field
    }

    get isEmpty():boolean {

        if ([
            ConditionType.IS_NULL,
            ConditionType.NOT_NULL
        ].includes(this._type)) {
            return false
        }

        if (_.isNil(this.type)) return true

        switch (typeof this.value) {

            case "number":
            case "string":
                // all good
                break

            case "boolean":
                return _.isNil(this.value)

            case "object":
                if (_.isEmpty(this.value)) return true
                break

            case 'undefined':
                return true

            default:
                console.warn("Unknown condition value type", typeof this.value, "value=", this.value, this)
                break

        }

        return false
    }

    isMetBy(model: ModelEntity): boolean {
        if (!this._field) return true

        const value = model[this._field.fieldId as keyof {}]

        let res = true
        switch (this._type) {
            case ConditionType.BOOLEAN_EQUALS:
                res = this.value === value
                break

            case ConditionType.NOT_NULL:
                res = !_.isNil(value)
                break

            case ConditionType.IS_NULL:
                res = _.isNil(value)
                break

            case ConditionType.INCLUDES:
                res = this.value.includes(value)
                break

            case ConditionType.EXCLUDES:
                res = !this.value.includes(value)
                break

            case ConditionType.ARRAY_INTERSECTION:
                res = _.intersection(this.value, value).length > 0
                break

            case ConditionType.NUMBER_GREATER_THAN:
                res = value > this.value
                break

            case ConditionType.NUMBER_LESS_THAN:
                res = value < this.value
                break

            case ConditionType.NUMBER_EQUALS:
                res = value === this.value
                break
            case ConditionType.NUMBER_BETWEEN:
                res = this.value?.greaterThan <= value && value <= this.value?.lessThan
                break

            case ConditionType.STRING_EQUALS:
                res = value === this.value
                break

            case ConditionType.STRING_CONTAINS:
                res = (value as string).includes(this.value)
                break

            case ConditionType.DATE_FIXED_BEFORE:
                res = (value as Moment).isBefore(this.value, 'day')
                break

            case ConditionType.DATE_FIXED_AFTER:
                res = (value as Moment).isAfter(this.value, 'day')
                break

            case ConditionType.DATE_FIXED_EQUALS:
                res = (value as Moment).isSame(this.value, 'day')
                break

            case ConditionType.DATE_FIXED_RANGE: {
                const valueMoment = value as Moment
                res = valueMoment.isSameOrAfter(this.value.start, 'day') && valueMoment.isSameOrBefore(this.value.end, 'day')
            } break

            case ConditionType.DATE_RELATIVE_RANGE: {
                const start = moment().add(this.value.start, 'days')
                const end = moment().add(this.value.end, 'days')
                const valueMoment = value as Moment
                res = valueMoment.isSameOrAfter(start, 'day') && valueMoment.isSameOrBefore(end, 'day')
            } break

            case ConditionType.DATE_RELATIVE_AFTER: {
                const relativeValue = moment().add(this.value, 'days')
                res = (value as Moment).isAfter(relativeValue, 'day')
            } break

            case ConditionType.DATE_RELATIVE_BEFORE: {
                const relativeValue = moment().add(this.value, 'days')
                res = (value as Moment).isBefore(relativeValue, 'day')
            } break

            case ConditionType.DATE_RELATIVE_EQUALS: {
                const relativeValue = moment().add(this.value, 'days')
                res = (value as Moment).isSame(relativeValue, 'day')
            } break

            case ConditionType.DATE_RELATIVE_PERIOD: {
                let relativeValue: any
                switch (this.value.relativity) {
                    case DateRangeRelativity.THIS:
                        relativeValue = {
                            start   : moment().startOf(this.value.period),
                            end     : moment().endOf(this.value.period)
                        }
                        break

                    case DateRangeRelativity.LAST:
                        relativeValue = {
                            start   : moment().subtract(1, this.value.period).startOf(this.value.period),
                            end     : moment().subtract(1, this.value.period).endOf(this.value.period)
                        }
                        break
                }

                const valueMoment = value as Moment
                res = valueMoment.isSameOrAfter(relativeValue.start, 'day') && valueMoment.isSameOrBefore(relativeValue.end, 'day')
            } break
        }

        return res
    }

    toJSON(params: ConditionsToJsonParams = {}) {
        const res: any = {
            id                  : this.id,
            // fieldId         : this._field?.fieldId,
            systemFieldId       : this._field?.systemFieldId,
            type                : this.type,
            value               : this.value,
        }

        if (params.addCustomFieldConfig) {
            const customField = this._field?.isCustom ? gd.session.customFields.get(this._field.fieldId) : undefined

            if (customField) {
                res.customFieldConfig   = customField.toJSON()
            }
        }

        return res
    }
}
